import { useEffect, useRef } from 'react';
import type { EventDto } from '@odo/shared-ui';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface DeleteEventModalProps {
    event: EventDto;
    onConfirm: () => void;
    onClose: () => void;
    isPending: boolean;
}

export function DeleteEventModal({ event, onConfirm, onClose, isPending }: DeleteEventModalProps) {
    // Autofocus the safe action (Cancel) - destructive defaults shouldn't be auto-focused.
    const cancelRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        cancelRef.current?.focus();
    }, []);

    useEscapeKey(onClose, !isPending);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-event-modal-title"
            className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex justify-center items-center"
        >
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[460px] p-6">
                <h2 id="delete-event-modal-title" className="text-lg font-bold text-gray-800 mb-2">
                    Delete event
                </h2>
                <p className="text-sm text-gray-600">
                    Delete "{event.title}" ({event.id})? This permanently removes the event and
                    cannot be undone.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                    Every sign-up recorded for it goes too: events.event_signups cascades on delete,
                    so you also lose the record of who had registered. Disable the event instead if
                    you only want it off the banners.
                </p>
                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        ref={cancelRef}
                        type="button"
                        onClick={onClose}
                        disabled={isPending}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={isPending}
                        className="bg-red-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isPending ? 'Deleting…' : 'Delete permanently'}
                    </button>
                </div>
            </div>
        </div>
    );
}
